/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import cx from 'classnames';
import { TableInstance } from 'react-table';
import { GenericDataType, styled, t } from '@superset-ui/core';
import { ClassWithColorSign, getNameType, listColumnLanguageMapping } from 'src/kvUtil';
import { checkSignPercent, getKVFormatNumber } from 'plugins/plugin-chart-echarts/src/utils/convertInteger';
import { getKVFormatNumberFunc } from 'plugins/plugin-chart-table-web/src/convertInteger';

interface TableCollectionProps {
	getTableProps: (userProps?: any) => any;
	getTableBodyProps: (userProps?: any) => any;
	prepareRow: TableInstance['prepareRow'];
	headerGroups: TableInstance['headerGroups'];
	rows: TableInstance['rows'];
	columns: TableInstance['column'][];
	// coltypes: TableInstance['coltypes'][];
	colType?: GenericDataType;
	loading: boolean;
	highlightRowId?: number;
	columnsForWrapText?: string[];
	formData?: any;
	isTableDetail?: boolean;
}

export const Table = styled.table`
	${({ theme }) => `
    background-color: ${theme.colors.grayscale.light5};
    border-collapse: separate;
    border-radius: ${theme.borderRadius}px;

    thead > tr > th {
      border: 0;
    }

    tbody {
      tr:first-of-type > td {
        border-top: 0;
      }
    }
    th {
      background: ${theme.colors.grayscale.light5};
      position: sticky;
      top: 0;

      &:first-of-type {
        padding-left: ${theme.gridUnit * 4}px;
      }

      &.xs {
        min-width: 25px;
      }
      &.sm {
        min-width: 50px;
      }
      &.md {
        min-width: 75px;
      }
      &.lg {
        min-width: 100px;
      }
      &.xl {
        min-width: 150px;
      }
      &.xxl {
        min-width: 200px;
      }

      span {
        white-space: nowrap;
        display: flex;
        align-items: center;
        line-height: 2;
      }

      svg {
        display: inline-block;
        position: relative;
      }
    }

    td {
      &.xs {
        width: 25px;
      }
      &.sm {
        width: 50px;
      }
      &.md {
        width: 75px;
      }
      &.lg {
        width: 100px;
      }
      &.xl {
        width: 150px;
      }
      &.xxl {
        width: 200px;
      }
    }

    .table-cell-loader {
      position: relative;

      .loading-bar {
        background-color: ${theme.colors.secondary.light4};
        border-radius: 7px;

        span {
          visibility: hidden;
        }
      }

      .empty-loading-bar {
        display: inline-block;
        width: 100%;
        height: 1.2em;
      }
    }

    .actions {
      white-space: nowrap;
      min-width: 100px;

      svg,
      i {
        margin-right: 8px;

        &:hover {
          path {
            fill: ${theme.colors.primary.base};
          }
        }
      }
    }

    .table-row {
      .actions {
        opacity: 0;
        font-size: ${theme.typography.sizes.xl}px;
        display: flex;
      }

      &:hover {
        background-color: ${theme.colors.secondary.light5};

        .actions {
          opacity: 1;
          transition: opacity ease-in ${theme.transitionTiming}s;
        }
      }
    }

    .table-row-selected {
      background-color: ${theme.colors.secondary.light4};

      &:hover {
        background-color: ${theme.colors.secondary.light4};
      }
    }

    .table-cell {
      font-feature-settings: 'tnum' 1;
      text-overflow: ellipsis;
      overflow: hidden;
      max-width: 320px;
      line-height: 1;
      vertical-align: middle;
      &:first-of-type {
        padding-left: ${theme.gridUnit * 4}px;
      }
      &__wrap {
        white-space: normal;
      }
      &__nowrap {
        white-space: nowrap;
      }
    }

    @keyframes loading-shimmer {
      40% {
        background-position: 100% 0;
      }

      100% {
        background-position: 100% 0;
      }
    }
  `}
`;

Table.displayName = 'table';

//KR2-47457: Với các tên cột có tên trong list dưới thì sẽ thêm class để chỉ giá trị là âm hoặc dương
//const ClassWithColorSign = ['sale_rate', 'item2', 'item3'];

function getClassNameForColumn(columnName: string = '', value: string) {
	if (ClassWithColorSign.includes(columnName)) {
		return 'kv-view-as-table-cell-' + checkSignPercent(value);
	}
	return '';
}

export default React.memo(
	({ getTableProps, getTableBodyProps, prepareRow, headerGroups, columns, rows, loading, highlightRowId, columnsForWrapText, formData, isTableDetail }: TableCollectionProps) => {
		//KR2-48902: Xử lý các kiểu N/A về dạng: string là rỗng, number là 0
		columns.forEach((column) => {
			if (column && column.colType === 0) {
				//Dạng number
				rows.forEach((row) => {
					const value = row.values[column.id];
					// Nếu giá trị là null, gán bằng 0
					if (!value && value != 0) {
						row.values[column.id] = 0;
					}
				});
			} else if (column && column.colType === 1) {
				//Dạng string
				rows.forEach((row) => {
					const value = row.values[column.id];
					// Nếu giá trị là null, gán bằng ''
					if (!value && value != '') {
						row.values[column.id] = '';
					}
				});
			}
		});

		//KR2-48902: Lấy formater số nếu có option từ KV Options
		function getFormatType(id: string) {
			let vsTableFormatter: any = {};
			if (formData?.format_number_type_vstable && formData?.format_number_type_vstable.length > 1 && formData?.format_number_type_vstable_mapping) {
				const mapping = JSON.parse(formData?.format_number_type_vstable_mapping);

				//Remove chuỗi __1 week ago (các chuỗi của kỳ so sánh)
				id = id.replace(/__[^_]+$/, '');
				const filteredElements: any = Object.values(mapping).filter((element: any) => element['metric'] == id);
				//Nếu tồn tại mapping thì sẽ sử dụng hàm format của mapping này
				if (filteredElements && filteredElements.length > 0) {
					const formatTypeIndex = filteredElements[0]['formatindex'];
					if (formData?.format_number_type_vstable.length > filteredElements[0]['formatindex']) {
						vsTableFormatter = getKVFormatNumberFunc(formData?.format_number_type_vstable[formatTypeIndex], {});
					} else {
						vsTableFormatter = getKVFormatNumberFunc(formData?.format_number_type_vstable[0], {});
					}
				} else {
					//Nếu không tồn tại mapping thì sẽ sử dụng mặc định là index 0
					vsTableFormatter = getKVFormatNumberFunc(formData?.format_number_type_vstable[0], {});
				}
			} else if (formData?.format_number_type_vstable && formData?.format_number_type_vstable.length > 0) {
				vsTableFormatter = getKVFormatNumberFunc(formData?.format_number_type_vstable[0], {});
			}

			return vsTableFormatter;
		}

		// //KR2-48902: Xử lý xác định kiểu số, string, date của một cột trong bảng
		// columns.forEach((column) => {
		// 	const columnValues = rows.map((row) => {
		// 		return row.values[column.id];
		// 	});

		// 	// Kiểm tra kiểu dữ liệu của cột
		// 	if (column.id == 'txn_date') {
		// 		console.log('112321');
		// 	}
		// 	let colType = '';
		// 	if (columnValues.every((value) => checkType(value) === 'number')) {
		// 		colType = 'number';
		// 	} else if (columnValues.every((value) => checkType(value) == 'date')) {
		// 		colType = 'date';
		// 	} else if (columnValues.every((value) => checkType(value) === 'string')) {
		// 		colType = 'string';
		// 	}

		// 	if (headerGroups && headerGroups.length > 0) {
		// 		const colindex = columns.findIndex((col) => col.id === column.id);
		// 		headerGroups[0].headers[colindex]['type'] = colType;
		// 	}
		// });

		return (
			<Table {...getTableProps()} className='table table-hover' data-test='listview-table'>
				<thead>
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column: any, index) => {
								let convertHeader = '';
								const headerName = column?.Header || '';
								if (headerName && isTableDetail && typeof headerName !== 'object') {
									if (headerName?.includes('ago') || headerName?.includes('comparison')) {
										convertHeader = t('Comparison period');
									} else {
										convertHeader = listColumnLanguageMapping[headerName] ? t(`${listColumnLanguageMapping[headerName]}`) : t(column.Header);
									}
								}
								//KR2-48876: xử lý class cho header view as table
								let colType = column?.['colType'];
								let sortIcon = <i className='fas fa-sort fa-fw kv-sort-icon'></i>;
								if (column.isSorted && column.isSortedDesc) {
									sortIcon = <i className='fas fa-sort-up fa-fw kv-sort-icon'></i>;
								} else if (column.isSorted && !column.isSortedDesc) {
									sortIcon = <i className='fas fa-sort-down fa-fw kv-sort-icon'></i>;
								}
								return column.hidden ? null : (
									<th
										{...column.getHeaderProps(column.canSort ? column.getSortByToggleProps() : {})}
										data-test='sort-header'
										className={cx(column.size, `kv-cell-${index + 1}`, `kv-cell-${getNameType(colType)}`)}>
										<span>
											{' '}
											{!isTableDetail ? (
												<>
													{column.render('Header')}
													{column.canSort && sortIcon}
												</>
											) : (
												<>
													{convertHeader}
													{column.canSort && sortIcon}
												</>
											)}
										</span>
									</th>
								);
							})}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{loading &&
						rows.length === 0 &&
						[...new Array(12)].map((_, i) => (
							<tr key={i}>
								{columns.map((column, i2) => {
									if (column.hidden) return null;
									return (
										<td
											key={i2}
											className={cx(
												'table-cell',
												{
													'table-cell-loader': loading,
												},
												`kv-cell-${i2 + 1}`
											)}>
											<span className='loading-bar empty-loading-bar' role='progressbar' aria-label='loading' />
										</td>
									);
								})}
							</tr>
						))}
					{rows.length > 0 &&
						rows.map((row) => {
							prepareRow(row);
							// @ts-ignore
							const rowId = row.original.id;

							return (
								<tr
									data-test='table-row'
									{...row.getRowProps()}
									className={cx('table-row', {
										'table-row-selected': row.isSelected || (typeof rowId !== 'undefined' && rowId === highlightRowId),
									})}>
									{row.cells.map((cell, index) => {
										if (index === 0) cell;
										if (cell.column.hidden) return null;
										const columnCellProps = cell.column.cellProps || {};
										const isWrapText = columnsForWrapText?.includes(cell.column.Header as string);
										let colType = cell.column?.['colType'];
										let vsTableFormatter = getFormatType(cell.column?.['id']);

										return (
											<td
												data-test='table-row-cell'
												className={cx(
													`table-cell table-cell__${isWrapText ? 'wrap' : 'nowrap'} ${getClassNameForColumn(cell?.column?.Header?.toString(), cell?.value)}`,
													{
														'table-cell-loader': loading,
														[cell.column.size || '']: cell.column.size,
													},
													`kv-cell-${index + 1}`,
													`kv-cell-${getNameType(colType)}`
												)}
												{...cell.getCellProps()}
												{...columnCellProps}>
												<span className={cx({ 'loading-bar': loading })} role={loading ? 'progressbar' : undefined}>
													{/* <span data-test='cell-text'>{cell.value}</span> */}
													{/* Kiểu string (dynamic range) hoặc number thì mới format */}
													<span data-test='cell-text'>
														{typeof vsTableFormatter === 'function' && !ClassWithColorSign.includes(cell.column.id) && colType in [0, 1]
															? vsTableFormatter(cell.value)
															: cell.render('Cell')}
													</span>
												</span>
											</td>
										);
									})}
								</tr>
							);
						})}
				</tbody>
			</Table>
		);
	}
);
